// ============================================================
// Zentryx Dominó - Core Types
// All shared types, interfaces and enums for the game
// ============================================================

/** A single domino tile value (0-6 for double-6 set) */
export type PipValue = 0 | 1 | 2 | 3 | 4 | 5 | 6;

/** Represents a domino tile with two halves */
export interface ITile {
  /** Top/Left pip value */
  a: PipValue;
  /** Bottom/Right pip value */
  b: PipValue;
  /** Unique ID for this tile instance */
  id: string;
}

/** Which end of the board chain */
export type BoardEnd = 'left' | 'right';

/** A tile placed on the board with position info */
export interface IBoardTile {
  tile: ITile;
  /** The value exposed on the left side of this tile in the chain */
  leftValue: PipValue;
  /** The value exposed on the right side of this tile in the chain */
  rightValue: PipValue;
}

/** Player types */
export type PlayerType = 'human' | 'ai';

/** AI Difficulty levels */
export enum AIDifficulty {
  Easy = 'easy',
  Medium = 'medium',
  Hard = 'hard',
}

/** Player representation */
export interface IPlayer {
  id: string;
  name: string;
  type: PlayerType;
  hand: ITile[];
  avatar: string;
  difficulty?: AIDifficulty;
}

/** Result of attempting to play a tile */
export interface IPlayResult {
  success: boolean;
  message?: string;
  /** Which end the tile was placed on */
  end?: BoardEnd;
  /** The tile as oriented on the board */
  boardTile?: IBoardTile;
}

/** State of the game */
export enum GameState {
  Idle = 'idle',
  Dealing = 'dealing',
  Playing = 'playing',
  WaitingForPlayer = 'waiting_for_player',
  WaitingForAI = 'waiting_for_ai',
  RoundOver = 'round_over',
  GameOver = 'game_over',
}

/** Game mode */
export enum GameMode {
  VsAI = 'vs_ai',
  Online1v1 = 'online_1v1',
  Pairs2v2 = 'pairs_2v2',
}

/** End-of-round result */
export interface IRoundResult {
  winnerId: string | null; // null if tie
  winnerName: string;
  isDraw: boolean;
  isBlocked: boolean;
  scores: { playerId: string; name: string; remainingPips: number }[];
  coinsEarned: number;
  xpEarned: number;
}

/** Rank tiers for progression */
export enum RankTier {
  Bronze = 'bronze',
  Silver = 'silver',
  Gold = 'gold',
  Diamond = 'diamond',
  Legend = 'legend',
}

/** Player profile data (persisted) */
export interface IPlayerProfile {
  name: string;
  avatarId: string;
  coins: number;
  gems: number;
  xp: number;
  level: number;
  rank: RankTier;
  wins: number;
  losses: number;
  draws: number;
  gamesPlayed: number;
  winStreak: number;
  bestWinStreak: number;
  ownedSkins: string[];
  equippedTileSkin: string;
  equippedBoardSkin: string;
  equippedAvatar: string;
  dailyMissionsCompleted: number;
  lastDailyReset: string; // ISO date
}

/** Skin item for the shop */
export interface ISkinItem {
  id: string;
  name: string;
  category: 'tiles' | 'boards' | 'avatars' | 'effects';
  price: number;
  currency: 'coins' | 'gems';
  preview: string;
  requiredLevel?: number;
  requiredWins?: number;
}

/** Game events emitted via EventBus */
export enum GameEvents {
  // Turn events
  TurnStart = 'turn:start',
  TurnEnd = 'turn:end',
  TilePlayRequested = 'tile:play_requested',
  TilePlayed = 'tile:played',
  TileInvalid = 'tile:invalid',
  PlayerPassed = 'player:passed',

  // Game flow
  GameStart = 'game:start',
  DealComplete = 'deal:complete',
  RoundEnd = 'round:end',
  GameOver = 'game:over',

  // Economy
  CoinsChanged = 'coins:changed',
  XPGained = 'xp:gained',
  LevelUp = 'level:up',
  RankUp = 'rank:up',

  // UI
  ShowToast = 'ui:toast',
  ShowReward = 'ui:reward',

  // Audio
  PlaySFX = 'audio:sfx',
  PlayMusic = 'audio:music',
}
